import { takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { AppTextService } from '@fi-shared/app-text/app-text.service';
import { CheckboxComponent, CheckboxModel } from '@fi-shared/checkbox';
import {
    AccessRightFilterCollectionModel,
    IAccessRightSelectionFiltersCollection,
    ILightWeightObject,
    ILightWeightUser,
    LocalSearchDataModel,
    LocalSearchItemModel,
    LocalSearchModel,
    SelectionFilterModel,
} from '@fi-shared/models';
import { AccessRightTabs, ObjectTypes } from '@fi-shared/types';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { DropdownSettingsModelV2 } from '@fi-shared/dropdown';
import { AccessRightService } from '@fi-shared/fi-shared-services/access-right.service';
import { FiSnackBarService } from '@fi-shared/fi-shared-services';
import { AccessRightFilterSettings } from './access-right-filter-settings';
import { IInitialSelectionFilterSettings, ISelectionFilterUserSettings } from './access-right-filters';
import { IAccessRightFilterCollection } from '@fi-shared/models/access-right-filter-collection.model';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { FiCheckbox2AdapterService } from '@fi-shared/fi-checkbox2/fi-checkbox2-adapter.service';
import { isDefined } from '@fi-shared/utils';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { ModuleAccessLevel } from '@fi-shared/access-right-filters/module-access-level';
import { FrontendMode, MobileAppHelperBaseService } from '@fi-shared/fi-shared-services/mobile-app-helper-base.service';
import { IFiHttpPromise } from '@fi-shared/to-fi-promise';
import {
    FiAccessRightHelperService,
    IAccessRightsCoreFiltersErrors,
} from '@fi-shared/fi-shared-services/fi-access-right-helper.service';

export enum AccessRightFiltersSourceType {
    Common = 0,
    ExtendedRights = 1,
    RequestAccess = 2,
}

@Component({
    selector: 'fi-access-right-filters',
    templateUrl: './access-right-filters.component.html',
    styleUrls: ['./access-right-filters.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-access-right-filters' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: AccessRightFiltersComponent,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: AccessRightFiltersComponent,
            multi: true,
        },
    ],
    standalone: false,
})
export class AccessRightFiltersComponent
    extends ControlValueAccessorValidatorBase<IAccessRightFilterCollection>
    implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
    @Input() ngModel: IAccessRightFilterCollection;
    @Input() isTooltipHidden: boolean = false;
    @Input() isHeaderHidden: boolean = false;
    @Input() isShared: boolean = true;
    @Input() accessRightFiltersSettings: AccessRightFilterSettings;
    @Input() hideTabs: boolean = false;
    @Input() disableUncheck: boolean = false;
    @Input() quickChoiceTitleId: string = 'access_rights.share_with';
    @Input() autoSelectDefault: boolean = false;
    @Input() showUserSelect: boolean = true;
    @Input() focusNewQuickSelection: boolean = false;
    @Input() hideTooltipObservable: Observable<void> = new Observable<void>();
    @Input() chooseUnitsColleaguesTextId = 'access_rights.and_or_units_colleagues';
    @Input() onResetSelection$: Observable<void> = new Observable<void>();
    @Input() accessRightFiltersSourceType = AccessRightFiltersSourceType.Common;
    @Output('quickChoiceChange') quickChoiceChangeEmitter: EventEmitter<string> = new EventEmitter<string>();
    @ViewChild('createSelectionCheckbox', { static: false }) createSelectionCheckbox: CheckboxComponent;
    @ViewChild('updateSelectionCheckbox', { static: false }) updateSelectionCheckbox: CheckboxComponent;
    initAccess$: BehaviorSubject<IAccessRightFilterCollection> = new BehaviorSubject<IAccessRightFilterCollection>(
        undefined,
    );
    updateNotAvailableIds$: BehaviorSubject<IAccessRightFilterCollection> =
        new BehaviorSubject<IAccessRightFilterCollection>(undefined);
    hideTooltipSubscription: Subscription;
    isTooltipShown$ = new BehaviorSubject(false);

    onInitialized$ = new Subject<void>();
    SelectedTabId: any;
    AccessRightTabsEnum = AccessRightTabs;

    get isAccessRequestMode() {
        return this.accessRightFiltersSourceType === AccessRightFiltersSourceType.RequestAccess;
    }

    private _selectedFilterId: number;
    get selectedFilterId() {
        return this._selectedFilterId;
    }
    set selectedFilterId(v) {
        if (this._selectedFilterId !== v) {
            this._selectedFilterId = v;
            this.quickChoiceChangeEmitter.emit(this.getSelectedSelectionTitle());
        }
    }

    loaded$ = new BehaviorSubject(false);

    // data for 'Update quick selection' mode
    SelectedSelectionFilterData: {
        Id?: number;
        Name?: string;
        IsDefault?: boolean;
    } = {};

    // selected selection filter name to display
    SelectedSelectionFilterName: string = '';

    ShowUpdateSelectionFilter: boolean = false;
    CreateSelectionFilterModel: CheckboxModel;
    UpdateSelectionFilterModel: CheckboxModel;

    SelectionFiltersListData = new DropdownSettingsModelV2<IAccessRightSelectionFiltersCollection>({
        filterBy: 'Name',
    });

    CcmUserSearch = new LocalSearchModel({
        DataDisplayFields: ['Name'],
        DataSearchFields: ['Name'],
    });

    CcmBusinessUnitAndClusterSearch = new LocalSearchModel({
        DataDisplayFields: ['Name'],
        DataSearchFields: ['Name'],
    });

    SelectedUsers: LocalSearchItemModel[] = [];
    SelectedBusinessUnitsAndClusters: LocalSearchItemModel[] = [];
    initialSelectionFilterSettings: IInitialSelectionFilterSettings = {
        RoleIds: [],
        BusinessAreaIds: [],
        AreaIds: [],
    };

    UserData: LocalSearchDataModel;
    BusinessUnitsData: LocalSearchDataModel;
    ClustersData: LocalSearchDataModel;

    HasAreaRights: boolean = false;

    private wasDialogShown = false;
    isSharedWithExtraColleaguesOrUnits: boolean = false;

    private areaIdsWithChildren: number[] = [];

    private shouldSaveSelectionFilterUserSetting = false;
    selectionFilterUserSetting: ISelectionFilterUserSettings = {
        IsAllDefaultForPosts: true,
    };

    protected availableAccessRightFilterCollection: IAccessRightFilterCollection;

    ObjectTypes = ObjectTypes;

    private accessLevel: ModuleAccessLevel;

    constructor(
        private injector: Injector,
        private accessRightService: AccessRightService,
        private fiDialogsService: FiDialogsService,
        private snackBar: FiSnackBarService,
        protected appTextService: AppTextService,
        private checkbox2AdapterService: FiCheckbox2AdapterService,
        private userDataService: FiUserDataService,
        private mobileAppHelperService: MobileAppHelperBaseService,
        protected fiAccessRightHelperService: FiAccessRightHelperService,
    ) {
        super(injector);

        this.CreateSelectionFilterModel = new CheckboxModel({
            Name: this.appTextService.get('access_rights.save_as_selection'),
        });
        this.UpdateSelectionFilterModel = new CheckboxModel({
            Name: appTextService.get('access_rights.update_selection'),
        });

        this.UserData = new LocalSearchDataModel({
            Header: appTextService.get('common.colleagues'),
            ObjectType: ObjectTypes.User,
        });

        this.BusinessUnitsData = new LocalSearchDataModel({
            Header: appTextService.get('common.units'),
            ObjectType: ObjectTypes.BusinessUnit,
        });

        this.ClustersData = new LocalSearchDataModel({
            Header: appTextService.get('common.cluster'),
            ObjectType: ObjectTypes.Cluster,
        });
    }

    ngOnInit(): void {
        if (!this.useThumbThacks()) this.selectionFilterUserSetting.IsAllDefaultForPosts = false;

        if (this.disableUncheck) {
            this.initialSelectionFilterSettings.RoleIds = this.ngModel.AccessRightCoreFilter.RoleIds.slice(0);
            this.initialSelectionFilterSettings.BusinessAreaIds =
                this.ngModel.AccessRightCoreFilter.BusinessAreaIds.slice(0);
            this.initialSelectionFilterSettings.AreaIds = this.ngModel.AccessRightCoreFilter.AreaIds.slice(0);
        }

        Promise.all([this._initFilters(), this._initAccessRights()]).then(() => {
            this.SetAccessRightFilters(this.ngModel);
            if (this.autoSelectDefault) {
                /**
                 * https://chainformation.atlassian.net/browse/FI-1694
                 * when autoSelectDefault is set to true and there is no default selection - manual selection tab
                 * should be selected by default
                 */
                if (this.selectDefaultQuickChoice() === true) {
                    this.SelectTabQuickSelection();
                } else {
                    this.initSelectedTab();
                }
            } else if (this.checkAccessRightFilterCollectionQuickChoice() === true) {
                /*
                https://chainformation.atlassian.net/browse/FI-1627
                when editing or creating a page the name of the quick current selection should be shown under "Sharing selection", instead of "Default selection".
                */
                this.SelectTabQuickSelection();
            } else {
                this.initSelectedTab();
            }
            this.onInitialized$.next();
            this.loaded$.next(true);
            this.cdr.detectChanges();
        });

        this.hideTooltipSubscription = this.hideTooltipObservable
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => this.isTooltipShown$.next(false));

        this.isSharedWithExtraColleaguesOrUnits =
            !!this.ngModel.AccessRightCoreFilter.BusinessUnitIds.length ||
            !!this.ngModel.AccessRightCoreFilter.ClusterIds.length ||
            !!this.ngModel.AccessRightUserFilter.UserIds.length;

        this.onResetSelection$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => (this.selectedFilterId = undefined));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.isTooltipHidden) {
            // this.showSharingSectionDialog();
            this.toggleIsTooltipShown();
        }

        if (changes.focusNewQuickSelection && changes.focusNewQuickSelection.currentValue) this.focusNewSelection();

        if (changes.isShared && !changes.isShared.isFirstChange()) this.doPropagateChanges();
    }

    ngAfterViewInit(): void {
        setTimeout(() => this.quickChoiceChangeEmitter.emit(this.getSelectedSelectionTitle()));
    }

    focusNewSelection(): void {
        this.SelectTab(this.AccessRightTabsEnum.ManualChoice);
        this.CreateSelectionFilterModel.Checked = true;
        setTimeout(() => {
            this.createSelectionCheckbox.setCheckedState(true);
        });
    }

    toggleIsTooltipShown(): void {
        if (!this.SelectionFiltersListData.dataSet.length && !this.isTooltipHidden && this.isSelectedTabQuickChoice()) {
            this.isTooltipShown$.next(true);
        } else {
            this.isTooltipShown$.next(false);
        }
    }

    private _initFilters(): Promise<any> {
        const promisePool = [];
        if (this.useThumbThacks()) {
            promisePool.push(
                this.accessRightService.getSelectionFilterUserSettings().then((userSettings) => {
                    this.selectionFilterUserSetting.IsAllDefaultForPosts = userSettings.IsAllDefaultForPosts;
                }),
            );
        }
        promisePool.push(
            this.LoadSelectionFilters().then((isSuccess) => {
                if (!isSuccess)
                    this.fiDialogsService.error({
                        messageText: this.appTextService.get('access_rights.selection_exists_warning'),
                    });
            }),
        );
        return Promise.all(promisePool);
    }

    usersOutOfScope: ILightWeightUser[] = [];
    clustersUnitsOutOfScope: ILightWeightObject[] = [];

    private _initAccessRights(): Promise<void> {
        const promise: IFiHttpPromise<AccessRightFilterCollectionModel> = (() => {
            if (this.accessRightFiltersSourceType === AccessRightFiltersSourceType.ExtendedRights)
                return this.accessRightService.GetExtendedAccessRightsForPublication();
            if (this.accessRightFiltersSourceType === AccessRightFiltersSourceType.RequestAccess)
                return this.accessRightService.getAccessRightsForRequestAccessSettings();
            return this.accessRightService.getAccessRightsForPublicationByObjectType(
                this.accessRightFiltersSettings.ObjectType,
            );
        })();

        return promise.then((accessRightFilterCollection) => {
            this.availableAccessRightFilterCollection = accessRightFilterCollection;
            this.accessLevel = accessRightFilterCollection.AccessLevel;

            this.initAccess$.next(accessRightFilterCollection);
            this.SetSearchControls(accessRightFilterCollection);
            // if user has only local rights then areaIds will be empty and we dont need to care about areas
            this.HasAreaRights =
                accessRightFilterCollection.AccessRightCoreFilter.AreaIds.length > 0 ||
                this.ngModel.AccessRightCoreFilter.AreaIds.length > 0;

            this.areaIdsWithChildren = accessRightFilterCollection.AccessRightCoreFilter.Areas.filter(
                (area) => area.ChildItems.length,
            ).map((area) => area.Id);
        });
    }

    useThumbThacks() {
        // https://chainformation.atlassian.net/browse/FI-2280
        // changes reverted by demand, maybe temporarily
        //return this.isShowAllTab();
        return false;
    }

    private initSelectedTab(): void {
        if (
            this.ngModel.AccessRightCoreFilter.RoleIds.length ||
            this.ngModel.AccessRightCoreFilter.BusinessAreaIds.length ||
            this.ngModel.AccessRightCoreFilter.AreaIds.length ||
            this.ngModel.AccessRightCoreFilter.BusinessUnitIds.length ||
            this.ngModel.AccessRightCoreFilter.ClusterIds.length ||
            this.ngModel.AccessRightUserFilter.UserIds.length /* ||
            !this.SelectionFiltersListData.dataSet.length*/
        ) {
            this.SelectTab(this.AccessRightTabsEnum.ManualChoice, true);
        } else {
            this.SelectTab(this.AccessRightTabsEnum.QuickChoice, true);
        }
    }

    isDefaultQuickChoiceSelecting: boolean = false;
    selectDefaultQuickChoice(): boolean {
        if (this.selectionFilterUserSetting.IsAllDefaultForPosts) return false;

        for (let i = 0; i < this.SelectionFiltersListData.dataSet.length; i++) {
            let selectionFilter = this.SelectionFiltersListData.dataSet[i];
            if (selectionFilter.IsDefault) {
                // https://chainformation.atlassian.net/browse/FI-3544
                this.isDefaultQuickChoiceSelecting = true;
                setTimeout(() => (this.isDefaultQuickChoiceSelecting = false));
                this._processSelectFilter(selectionFilter);
                this.selectedFilterIdChange();
                return true;
            }
        }
    }

    checkAccessRightFilterCollectionQuickChoice(): boolean {
        for (let i = 0; i < this.SelectionFiltersListData.dataSet.length; i++) {
            const selectionFilter = this.SelectionFiltersListData.dataSet[i];
            const isSelectionFilterFound = this.accessRightService.AreAccessRightFilterCollectionEqual(
                this.ngModel,
                selectionFilter.SelectionFilterSettingCollection,
                this.areaIdsWithChildren,
            );
            if (isSelectionFilterFound) {
                this._processSelectFilter(selectionFilter);
                this.selectedFilterIdChange();
                return true;
            }
        }
    }

    protected _processSelectFilter(selectionFilter): void {
        this.selectedFilterId = selectionFilter.Id;
        this.SelectedSelectionFilterData = {
            Id: selectionFilter.Id,
            Name: selectionFilter.Name,
            IsDefault: selectionFilter.IsDefault,
        };
    }

    isSelectedTabQuickChoice(): boolean {
        return this.SelectedTabId === this.AccessRightTabsEnum.QuickChoice;
    }

    isSelectedTabManualChoice(): boolean {
        return this.SelectedTabId === this.AccessRightTabsEnum.ManualChoice;
    }

    isShowCreateFilter(): boolean {
        return this.isSelectedTabManualChoice() && !this.selectedFilterId;
    }

    isShowUpdateFilter(): boolean {
        return this.isSelectedTabManualChoice() && !!this.SelectedSelectionFilterData.Id && !this.selectedFilterId;
    }

    private getSelectedSelectionTitle(): string {
        const quickChoice = this.getSelectedQuickChoice();
        return !quickChoice ? this.appTextService.get('access_rights.manual_selection') : quickChoice.Name;
    }

    setSelectedTab(tabId): void {
        this.SelectedTabId = tabId;
        //this.ngModel.SelectionMode = tabId;
    }

    SelectTab(tabId: number, preventPropagateChanges?: boolean) {
        this.setSelectedTab(tabId);

        if (this.isSelectedTabQuickChoice()) {
            this.updateQuickChoiceFromModel();
            this.toggleIsTooltipShown();
        }

        if (!preventPropagateChanges) this.doPropagateChanges();
    }

    SelectTabClick(tabId) {
        if (this.SelectedTabId !== tabId) this.SelectTab(tabId);
    }

    /*
    SelectTabManual() {
        this.SelectTab(this.AccessRightTabsEnum.ManualChoice);
    }
*/

    SelectTabQuickSelection() {
        this.SelectTab(this.AccessRightTabsEnum.QuickChoice);
    }

    updateQuickChoiceFromModel() {
        // https://chainformation.atlassian.net/browse/FI-3544 //todo don't run updateQuickChoiceFromModel on tab change?
        if (this.isDefaultQuickChoiceSelecting) return;
        this.selectedFilterId = null;
        this.SelectionFiltersListData.dataSet.some((selectionFilter) => {
            if (
                this.accessRightService.AreAccessRightFilterCollectionEqual(
                    this.ngModel,
                    selectionFilter.SelectionFilterSettingCollection,
                    this.areaIdsWithChildren,
                )
            ) {
                this._processSelectFilter(selectionFilter);
                return true;
            }
        });
    }

    GetSelectedSelectionFilter() {
        return this.SelectionFiltersListData.dataSet.find(
            (selectionFilter) => selectionFilter.Id === this.selectedFilterId,
        );
    }

    SetNgModelItems() {
        this.ngModel.AccessRightCoreFilter.BusinessUnitIds = [];
        this.ngModel.AccessRightCoreFilter.ClusterIds = [];
        this.ngModel.AccessRightUserFilter.UserIds = [];
        this.SelectedBusinessUnitsAndClusters.forEach((selectedItem) => {
            if (selectedItem.ObjectType === ObjectTypes.BusinessUnit) {
                this.ngModel.AccessRightCoreFilter.BusinessUnitIds.push(selectedItem.Id);
            } else if (selectedItem.ObjectType === ObjectTypes.Cluster) {
                this.ngModel.AccessRightCoreFilter.ClusterIds.push(selectedItem.Id);
            }
        });

        this.SelectedUsers.forEach((selectedUser) => {
            this.ngModel.AccessRightUserFilter.UserIds.push(selectedUser.Id);
        });

        this.doPropagateChanges();
    }

    SetAccessRightFilters(accessRightFilterCollection) {
        this.ngModel.AccessRightCoreFilter.RoleIds = accessRightFilterCollection.AccessRightCoreFilter.RoleIds.slice(0);
        this.ngModel.AccessRightCoreFilter.BusinessAreaIds =
            accessRightFilterCollection.AccessRightCoreFilter.BusinessAreaIds.slice(0);
        if (this.HasAreaRights) {
            //Special case as we only need to set it when user has area rights
            this.ngModel.AccessRightCoreFilter.AreaIds =
                accessRightFilterCollection.AccessRightCoreFilter.AreaIds.slice(0);
        }

        this.SelectedBusinessUnitsAndClusters = [];
        this.SelectedUsers = [];

        this.updateNotAvailableIds$.next(accessRightFilterCollection);
        this.accessRightService.SetAccessRightCoreFilterForSearchItems(
            accessRightFilterCollection.AccessRightCoreFilter.BusinessUnitIds,
            ObjectTypes.BusinessUnit,
            this.SelectedBusinessUnitsAndClusters,
            this.BusinessUnitsData,
        );

        this.accessRightService.SetAccessRightCoreFilterForSearchItems(
            accessRightFilterCollection.AccessRightCoreFilter.ClusterIds,
            ObjectTypes.Cluster,
            this.SelectedBusinessUnitsAndClusters,
            this.ClustersData,
        );

        if (this.disableUncheck) this.SelectedBusinessUnitsAndClusters.forEach((item) => (item.IsEnabled = false));

        if (this.showUserSelect) {
            this.accessRightService.SetAccessRightCoreFilterForSearchItems(
                accessRightFilterCollection.AccessRightUserFilter.UserIds,
                ObjectTypes.User,
                this.SelectedUsers,
                this.UserData,
            );
        }
        if (this.disableUncheck) this.SelectedUsers.forEach((item) => (item.IsEnabled = false));
    }

    LoadSelectionFilters() {
        const promise = this.accessRightService.GetSelectionFiltersForPublication(
            this.accessRightFiltersSettings.ModuleType,
            true,
        );
        promise.then((selectionFiltersCollection: IAccessRightSelectionFiltersCollection[]) => {
            this.SelectionFiltersListData.setDataSet(selectionFiltersCollection);
        });
        return promise;
    }

    SetSearchControls(accessRightFilterCollection) {
        this.UserData.Data = this.GetSearchItemCollection(
            accessRightFilterCollection.AccessRightUserFilter.Users,
            ObjectTypes.User,
        );
        this.usersOutOfScope = this.fiAccessRightHelperService.getOutOfScopeUsers(
            this.UserData.Data,
            this.ngModel.AccessRightUserFilter.Users,
        );
        this.UserData.Data.push(...this.GetSearchItemCollection(this.usersOutOfScope, ObjectTypes.User));

        this.CcmUserSearch.SearchData.push(this.UserData);

        this.BusinessUnitsData.Data = this.GetSearchItemCollection(
            accessRightFilterCollection.AccessRightCoreFilter.BusinessUnits,
            ObjectTypes.BusinessUnit,
        );
        const outOfScopeBusinessUnits = this.fiAccessRightHelperService.getOutOfScopeClusterAndUnit(
            this.BusinessUnitsData.Data,
            this.ngModel.AccessRightCoreFilter.BusinessUnits,
        );
        this.BusinessUnitsData.Data.push(
            ...this.GetSearchItemCollection(outOfScopeBusinessUnits, ObjectTypes.BusinessUnit),
        );

        this.CcmBusinessUnitAndClusterSearch.SearchData.push(this.BusinessUnitsData);

        this.ClustersData.Data = this.GetSearchItemCollection(
            accessRightFilterCollection.AccessRightCoreFilter.Clusters,
            ObjectTypes.Cluster,
        );
        const outOfScopeClusters = this.fiAccessRightHelperService.getOutOfScopeClusterAndUnit(
            this.ClustersData.Data,
            this.ngModel.AccessRightCoreFilter.Clusters,
        );
        this.ClustersData.Data.push(...this.GetSearchItemCollection(outOfScopeClusters, ObjectTypes.Cluster));
        this.CcmBusinessUnitAndClusterSearch.SearchData.push(this.ClustersData);

        this.clustersUnitsOutOfScope.push(...outOfScopeBusinessUnits, ...outOfScopeClusters);
    }

    GetSearchItemCollection(items: Array<any>, objectType) {
        return items.map((item) => {
            return new LocalSearchItemModel(
                item.Id,
                objectType === ObjectTypes.User ? item.FullName : item.Name,
                objectType,
                item.MediaBankPhysicalFileName ? item.MediaBankPhysicalFileName : undefined,
                item.MediaBankExtension ? item.MediaBankExtension : undefined,
            );
        });
    }

    selectedFilterIdChange(filterId?: number) {
        if (isDefined(filterId)) {
            this.selectedFilterId = filterId;
        }
        this.CreateSelectionFilterModel.Checked = false; // edit mode is gone when changing filter
        this.UpdateSelectionFilterModel.Checked = false; // edit mode is gone when changing filter
        this.ShowUpdateSelectionFilter = false;
        this.SelectedSelectionFilterData = {};

        const selectedSelectionFilter = this.GetSelectedSelectionFilter();
        if (selectedSelectionFilter) {
            this.accessRightService.removeNonExistingSelectionFilter(
                selectedSelectionFilter.SelectionFilterSettingCollection,
                this.availableAccessRightFilterCollection,
            );
            this.ShowUpdateSelectionFilter = true;
            this._processSelectFilter(selectedSelectionFilter);

            this.SetAccessRightFilters(selectedSelectionFilter.SelectionFilterSettingCollection);
            this.SetNgModelItems();

            if (!this.isSelectedFilterDefault() && this.useThumbThacks())
                setTimeout(() => this.saveSelectedQuickSelectionAsDefault());
        } else {
            this.ResetAccessRightFilter();
        }
    }

    getErrorMessage() {
        const defaultErrorMessage = this.appTextService.get('access_right_filter_not_valid');

        if (this.CreateSelectionFilterModel.Checked) {
            if (this.isSelectedTabManualChoice() && this.SelectedSelectionFilterName === '') {
                return this.appTextService.get('access_rights.quick_selection_name_required_message');
            }

            // validate unique filter name
            let filteredItems = this.SelectionFiltersListData.dataSet;

            for (let idx = 0; idx < filteredItems.length; idx++) {
                const selectionFilter = filteredItems[idx];
                // Filter name matches other filter name
                if (this.SelectedSelectionFilterName === selectionFilter.Name)
                    return this.appTextService.get('quick_selection_name_exists', {
                        '{0}': this.SelectedSelectionFilterName,
                    });
            }
        }

        return this.isValid ? null : defaultErrorMessage;
    }

    validateAccessRightFilterCollection() {
        const errorMessage = this.getErrorMessage();
        const isValid = !errorMessage;
        if (errorMessage)
            this.fiDialogsService.notify({
                headerText: this.appTextService.get('access_right_filter_not_valid_header'),
                messageText: errorMessage,
            });

        this.onTouched();
        return isValid;
    }

    get isValid() {
        return Object.keys(this.errors).length === 0;
    }

    get errors() {
        return this.ValidateAccessRights();
    }

    private ValidateAccessRights(): IAccessRightsCoreFiltersErrors {
        if (!this.loaded$.value) return {};

        let errors: IAccessRightsCoreFiltersErrors = this.fiAccessRightHelperService.validateAccessRightsCoreFilters(
            this.ngModel.AccessRightCoreFilter,
            {
                isShared: this.isShared,
                onlyRolesMode: this.isAccessRequestMode,
                accessRightUserFilter: this.ngModel.AccessRightUserFilter,
            },
        );

        if (
            this.mobileAppHelperService.frontEndMode !== FrontendMode.MobileApp &&
            this.SelectedTabId === this.AccessRightTabsEnum.QuickChoice &&
            !this.GetSelectedSelectionFilter()
        )
            errors.selection_filter_required = true;

        if (this.isAccessRequestMode) {
            // https://chainformation.atlassian.net/browse/FI-5616
            // don't show error tooltip for request access
            errors = {};
        }

        return errors;
    }

    doValidate(): ValidationErrors {
        const result = {};
        const errors = this.ValidateAccessRights();
        for (let prop in errors) {
            if (errors[prop]) {
                result[prop] = true;
            }
        }
        return result;
    }

    OnAccessRightFilterCollectionSaved() {
        if (
            this.isValid &&
            this.isSelectedTabManualChoice() &&
            (this.CreateSelectionFilterModel.Checked || this.UpdateSelectionFilterModel.Checked)
        )
            this.SaveSelectionFilterSettings();

        if (this.shouldSaveSelectionFilterUserSetting)
            this.accessRightService.saveSelectionFilterUserSettings(this.selectionFilterUserSetting);
    }

    OnAccessRightFilterCollectionSaving() {
        this.SelectTab(this.SelectedTabId);
        if (this.isSelectedTabManualChoice()) this.SetNgModelItems();
    }

    createSelectionFilterModel(): SelectionFilterModel {
        const selectionFilter = new SelectionFilterModel();
        selectionFilter.IsActivated = true;
        selectionFilter.InitializeSelectionFilterSettingCollection(this.ngModel);
        return selectionFilter;
    }

    saveSelectionFilterModel(selectionFilter: SelectionFilterModel) {
        return this.accessRightService.SaveSelectionFilter(selectionFilter, this.accessRightFiltersSettings.ModuleType);
    }

    SaveSelectionFilterSettings(saveAsDefault = false) {
        // check if we should update selection filter settings
        const selectionFilter = this.createSelectionFilterModel();
        if (this.UpdateSelectionFilterModel.Checked) {
            selectionFilter.Id = this.SelectedSelectionFilterData.Id;
            selectionFilter.Name = this.SelectedSelectionFilterData.Name;
            selectionFilter.IsDefault = this.SelectedSelectionFilterData.IsDefault;
        }
        if (this.CreateSelectionFilterModel.Checked) selectionFilter.Name = this.SelectedSelectionFilterName;

        if (saveAsDefault) selectionFilter.IsDefault = this.SelectedSelectionFilterData.IsDefault = true;

        return new Promise((resolve, reject) => {
            this.saveSelectionFilterModel(selectionFilter).then((selectionFilterId) => {
                if (selectionFilterId > 0) {
                    // waiting for selection filters to be loaded before selecting tab
                    this.LoadSelectionFilters().then((isSuccess) => {
                        if (isSuccess) {
                            this.selectedFilterId = null;
                            resolve(<any>selectionFilterId);
                        } else {
                            this.fiDialogsService.error({
                                messageText: this.appTextService.get('access_rights.selection_exists_warning'),
                            });
                            reject();
                        }
                    });
                } else {
                    reject();
                }
            });
        });
    }

    getCheckedIdsFromDataSet(dataSet: Array<any>) {
        return dataSet.filter((item) => item.Checked).map((item) => item.Id);
    }

    getCheckedIdsFromDataSetWithChildItems(dataSet: Array<any>, DataChildItemField) {
        let result: Array<any> = [];
        dataSet.forEach((item) => {
            if (item.Checked) result.push(item.Id);

            if (item[DataChildItemField])
                result = result.concat(
                    this.getCheckedIdsFromDataSetWithChildItems(item[DataChildItemField], DataChildItemField),
                );
        });
        return result;
    }

    rolesChanged() {
        this._internalChange();
        if (
            this.ngModel.AccessRightCoreFilter.RoleIds.length === 1 &&
            !this.ngModel.AccessRightCoreFilter.AreaIds.length &&
            !this.ngModel.AccessRightCoreFilter.BusinessAreaIds.length &&
            !this.SelectedUsers.length
        )
            this.selectUsersBusinessUnit();
    }

    businessAreasChanged() {
        this._internalChange();
        if (
            this.ngModel.AccessRightCoreFilter.BusinessAreaIds.length === 1 &&
            !this.ngModel.AccessRightCoreFilter.RoleIds.length &&
            !this.ngModel.AccessRightCoreFilter.AreaIds.length &&
            !this.SelectedUsers.length
        )
            this.selectUsersBusinessUnit();
    }

    areasChanged() {
        if (
            !this.isSharedWithExtraColleaguesOrUnits &&
            (this.ngModel.AccessRightCoreFilter.ClusterIds.length ||
                this.ngModel.AccessRightCoreFilter.BusinessUnitIds.length ||
                this.ngModel.AccessRightUserFilter.UserIds.length)
        )
            this.showConfirmationDialog();

        if (
            this.ngModel.AccessRightCoreFilter.AreaIds.length === 1 &&
            !this.ngModel.AccessRightCoreFilter.RoleIds.length &&
            !this.ngModel.AccessRightCoreFilter.BusinessAreaIds.length &&
            !this.SelectedUsers.length
        )
            this.selectUsersBusinessUnit();

        this._internalChange();
    }

    private selectUsersBusinessUnit() {
        if (this.accessLevel === ModuleAccessLevel.Local) {
            const businessUnitData = this.BusinessUnitsData.Data.find((businessUnit) => {
                return businessUnit.Id === +this.userDataService.userData.BusinessUnitId;
            });
            if (
                !this.SelectedBusinessUnitsAndClusters.some((selectedBusinessUnitAndCluster) => {
                    return (
                        selectedBusinessUnitAndCluster.Id === businessUnitData.Id &&
                        businessUnitData.ObjectType === selectedBusinessUnitAndCluster.ObjectType
                    );
                })
            ) {
                this.ngModel.AccessRightCoreFilter.BusinessUnitIds.push(+this.userDataService.userData.BusinessUnitId);
                this.SelectedBusinessUnitsAndClusters.push(<any>businessUnitData); // todo fix typings
                this.SelectedBusinessUnitsAndClusters = this.SelectedBusinessUnitsAndClusters.slice();
                this._internalChange();
            }
        }
    }

    selectedUsersChange($event) {
        this.SelectedUsers = $event;
        this.ngModel.AccessRightUserFilter.UserIds = this.SelectedUsers.map((item) => item.Id);
        if (!this.isSharedWithExtraColleaguesOrUnits && this.ngModel.AccessRightCoreFilter.AreaIds.length)
            this.showConfirmationDialog();

        this._internalChange();
    }

    selectedBusinessUnitsAndClustersChange($event) {
        this.ngModel.AccessRightCoreFilter.BusinessUnitIds = [];
        this.ngModel.AccessRightCoreFilter.ClusterIds = [];
        this.SelectedBusinessUnitsAndClusters = $event;
        this.SelectedBusinessUnitsAndClusters.forEach((selectedItem) => {
            if (selectedItem.ObjectType === ObjectTypes.BusinessUnit) {
                this.ngModel.AccessRightCoreFilter.BusinessUnitIds.push(selectedItem.Id);
            } else if (selectedItem.ObjectType === ObjectTypes.Cluster) {
                this.ngModel.AccessRightCoreFilter.ClusterIds.push(selectedItem.Id);
            }
        });
        if (!this.isSharedWithExtraColleaguesOrUnits && this.ngModel.AccessRightCoreFilter.AreaIds.length)
            this.showConfirmationDialog();

        this._internalChange();
    }

    _internalChange() {
        this.updateQuickChoiceFromModel();
        this.doPropagateChanges();
    }

    editQuickChoiceClick(filteredItem) {
        this.selectedFilterId = filteredItem.Id;
        this.selectedFilterIdChange();
        // this.selectedFilterDropdown.closeDropdown();
        this.SelectTab(this.AccessRightTabsEnum.ManualChoice);
    }

    deleteQuickChoiceClick(filteredItem) {
        this.fiDialogsService
            .confirm({
                headerText: this.appTextService.get('dialog.header_confirm'),
                messageText: this.appTextService.get('access_rights.delete_selection.confirm'),
            })
            .onYesButtonClick(() => {
                const isSelectedFilter = this.selectedFilterId === filteredItem.Id;
                this.accessRightService.DeleteSelectionFilter(filteredItem.Id).then(() => {
                    const promise = this.LoadSelectionFilters();

                    promise.then((isSuccess) => {
                        // waiting for selection filters to be loaded before selecting tab
                        if (isSuccess) {
                            if (isSelectedFilter) {
                                // if we delete the selected filter we reset evrything
                                this.selectedFilterId = null;
                                this.ResetAccessRightFilter();
                            }
                            this.SelectionFiltersListData.setDataSet([].concat(this.SelectionFiltersListData.dataSet));
                            this.cdr.detectChanges();
                        }
                    });
                });
            });
    }

    ResetAccessRightFilter() {
        this.ngModel.AccessRightCoreFilter.RoleIds = [];
        this.ngModel.AccessRightCoreFilter.BusinessAreaIds = [];
        this.ngModel.AccessRightCoreFilter.AreaIds = [];
        this.ngModel.AccessRightCoreFilter.BusinessUnitIds = [];
        this.ngModel.AccessRightCoreFilter.ClusterIds = [];
        this.ngModel.AccessRightUserFilter.UserIds = [];

        this.SelectedBusinessUnitsAndClusters = [];
        this.SelectedUsers = [];
        this.doPropagateChanges();
    }

    getSelectedQuickChoice() {
        if (this.selectedFilterId)
            return this.SelectionFiltersListData.dataSet.find((item) => {
                return item[this.SelectionFiltersListData.dataValueField] === this.selectedFilterId;
            });

        return null;
    }

    createFilterChangedChange() {
        if (this.updateSelectionCheckbox && this.updateSelectionCheckbox.isChecked())
            this.updateSelectionCheckbox.setCheckedState(false);
    }

    updateFilterCheckedChange() {
        if (this.createSelectionCheckbox && this.createSelectionCheckbox.isChecked())
            this.createSelectionCheckbox.setCheckedState(false);
    }

    getSelectedFilter() {
        return this.SelectionFiltersListData.dataSet.find((filters) => filters.Id === this.selectedFilterId);
    }

    isSelectedFilterDefault(): boolean {
        // if (!this.selectedFilterDropdown)
        //     return true;
        // this.selectedFilterDropdown.getSelectedItems();
        // return selectedFilter && selectedFilter.length > 0 && selectedFilter[0].IsDefault === true;
        if (!isDefined(this.selectedFilterId)) {
            return false;
        }

        const selectedFilter = this.getSelectedFilter();
        return selectedFilter?.IsDefault;
    }

    saveSelectedQuickSelectionAsDefault() {
        // const selectedFilter = this.selectedFilterDropdown.getSelectedItems();
        const selectedFilter = this.getSelectedFilter();

        if (isDefined(selectedFilter)) {
            const currentDefault = this.SelectionFiltersListData.dataSet.find((item) => item.IsDefault);
            const currentDefaultId = currentDefault ? currentDefault.Id : undefined;
            const newDefaultId = selectedFilter.Id;

            this.SelectionFiltersListData.dataSet.forEach((item) => (item.IsDefault = item.Id === newDefaultId));
            // this.selectedFilterDropdown.updateViewData();
            this._saveQuickSelectionDefaultId(newDefaultId, currentDefaultId);
        }
    }

    private _saveQuickSelectionDefaultId(id, undoDefaultId) {
        this.accessRightService
            .UpdateSelectionFilterIsDefault(id, this.accessRightFiltersSettings.ModuleType, true)
            .then(() => {
                if (undoDefaultId) {
                    this.snackBar
                        .showSnackbar({
                            textMessage: this.appTextService.get('access_rights_filters.default_updated'),
                            buttonText: this.appTextService.get('common.undo'),
                        })
                        .onAction()
                        .subscribe(() => {
                            this.SelectionFiltersListData.dataSet.forEach(
                                (item) => (item.IsDefault = item.Id == undoDefaultId),
                            );
                            // this.selectedFilterDropdown.updateViewData();
                            this.accessRightService.UpdateSelectionFilterIsDefault(
                                undoDefaultId,
                                this.accessRightFiltersSettings.ModuleType,
                                true,
                            );
                        });
                }
            });
    }

    saveNewSelectionAsDefaultClick() {
        const errorMessage = this.getErrorMessage();
        if (errorMessage) {
            this.fiDialogsService.error({ messageText: errorMessage });
        } else {
            this.SetNgModelItems();
            this.SaveSelectionFilterSettings(true).then((result: number) => {
                this.selectedFilterId = result;
                this.SelectedSelectionFilterName = '';
                this.createSelectionCheckbox.setCheckedState(false);
            });
        }
    }

    showConfirmationDialog() {
        if (!this.wasDialogShown) {
            this.fiDialogsService
                .confirm({
                    isNoButtonHidden: true,
                    headerText: this.appTextService.get('access_right_filter.selection_warning_dialog_header'),
                    messageText: this.appTextService.get('access_right_filter.selection_warning_dialog_message'),
                    yesButtonText: this.appTextService.get('common.understand_button_text'),
                })
                .afterClosed(() => (this.wasDialogShown = true));
        }
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
